/* eslint-disable max-lines-per-function */
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BornControl } from './BornControl'
import { DemographicFunctions } from './DemographicFunctions'
import { HousingControl } from './HousingControl'
import { ResidenceControl } from './ResidenceControl'

import { useProviderLoader } from '@/Providers'
import { FormNavigation } from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import { useAuthHook } from '@/hooks/useAuthHook'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import {
    DemographicFormData,
    FormsGroupsProps,
    OptionsField
} from '@/types/forms/'
import { useFormProgress } from '@/utils'

// Definición de las dependencias entre campos
const FIELD_DEPENDENCIES = {
    // Dependencias de nacimiento
    countryBorn: ['departmentBorn', 'cityBorn'],
    departmentBorn: ['cityBorn'],

    // Dependencias de residencia
    countryResidence: [
        'departmentResidence',
        'idCityResidence',
        'localidad',
        'timeResidence'
    ],
    departmentResidence: ['idCityResidence', 'localidad', 'timeResidence'],
    idCityResidence: ['timeResidence']
} as const

// Dependencias condicionales - solo se mantienen si se cumple la condición
const CONDITIONAL_DEPENDENCIES = {
    departmentResidence: {
        field: 'localidad',
        condition: '11' // Solo Bogotá
    },
    idCityResidence: {
        field: 'timeResidence',
        condition: 'BOGOTA' // Solo Bogotá ciudad
    },
    countryBorn: {
        field: 'cityBorn',
        condition: 'CO' // Solo Colombia
    }
    // Removido countryResidence -> zoneResidence porque son datos independientes
} as const

// Limpia los datos demográficos antes de enviarlos al backend, asegurando que solo Bogotá tenga los campos especiales
function cleanDemographicData(data: any): any {
    const cleanData = { ...data }
    const dependents = [
        { field: 'cityBorn', parent: 'departmentBorn' },
        { field: 'idCityResidence', parent: 'departmentResidence' },
        { field: 'localidad', parent: 'departmentResidence', value: '11' },
        {
            field: 'timeResidence',
            parent: 'idCityResidence',
            value: 'BOGOTA'
        }
        // Removido zoneResidence porque es un dato de vivienda independiente
    ]

    dependents.forEach(({ field, parent, value }) => {
        if (
            !cleanData[field] ||
            !cleanData[parent] ||
            (value && cleanData[parent] !== value)
        ) {
            delete cleanData[field]
        }
    })

    Object.keys(cleanData).forEach((key) => {
        if (
            cleanData[key] === undefined ||
            cleanData[key] === null ||
            cleanData[key] === ''
        ) {
            delete cleanData[key]
        }
    })

    return cleanData
}

export const DemographicForm = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): JSX.Element => {
    const { t } = useTranslation()
    const { control, handleSubmit, reset, watch, trigger, setValue } =
        useForm<DemographicFormData>()
    const { showToast } = useToast()
    const { idAsesor } = useAuthStore()
    const { idAspirant } = useAuthHook()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { showLoader, hideLoader } = useProviderLoader()
    const progress = useFormProgress<DemographicFormData>({
        watch,
        excludedFields: ['idAspirant'],
        conditionalFields: [
            {
                field: 'cityBorn',
                dependsOn: 'countryBorn',
                condition: 'CO'
            },
            {
                field: 'idCityResidence',
                dependsOn: 'countryResidence',
                condition: 'CO'
            },
            {
                field: 'timeResidence',
                dependsOn: 'idCityResidence',
                condition: 'BOGOTA'
            },
            {
                field: 'localidad',
                dependsOn: 'idCityResidence',
                condition: 'BOGOTA'
            }
        ]
    })
    const text = 'demographicInformation'
    const [bornCities, setBornCities] = useState<OptionsField[]>([])
    const [nationalityOptions, setNationalityOptions] = useState<
        OptionsField[]
    >(
        (() => {
            try {
                const cached = sessionStorage.getItem('nationalityOptions')
                if (!cached) return []
                const parsed = JSON.parse(cached) as OptionsField[]
                return parsed.filter(
                    (opt) =>
                        opt.value &&
                        opt.label &&
                        String(opt.value).trim() !== '' &&
                        String(opt.label).trim() !== '' &&
                        String(opt.label).toLowerCase() !== 'unknown' &&
                        String(opt.label).trim() !== '.'
                )
            } catch {
                return []
            }
        })()
    )
    const { fetchNationalitiesList } = DemographicFunctions()

    // Función para limpiar campos dependientes cuando el usuario hace cambios manuales
    const handleFieldChange = (field: string, newValue: any) => {
        const parentField = field as keyof typeof FIELD_DEPENDENCIES
        const dependentFields = FIELD_DEPENDENCIES[parentField]

        if (dependentFields) {
            dependentFields.forEach((dependentField) => {
                setValue(dependentField as keyof DemographicFormData, '')
            })
        }

        // Verificar dependencias condicionales
        const conditionalDep =
            CONDITIONAL_DEPENDENCIES[
            parentField as keyof typeof CONDITIONAL_DEPENDENCIES
            ]

        if (conditionalDep && newValue !== conditionalDep.condition) {
            setValue(conditionalDep.field as keyof DemographicFormData, '')
        }
    }

    // Función auxiliar para parsear cityBorn del formato "código-nombre" a solo código
    const parseCityBorn = (cityBorn: any): string => {
        if (!cityBorn || typeof cityBorn !== 'string') {
            return cityBorn || ''
        }

        if (cityBorn.includes('-')) {
            const [code] = cityBorn.split('-')

            return code
        }

        return cityBorn
    }

    useEffect(() => {
        const loadDemographicData = async (): Promise<Element | void> => {
            if (!idAspirant) {
                return
            }

            showLoader()

            try {
                const response =
                    await aspirantsApi.getDemographicData(idAspirant)

                if (!response.data) {
                    hideLoader()

                    return
                }

                // Parsear cityBorn si viene en formato "código-nombre" para extraer solo el código
                response.data.cityBorn = parseCityBorn(response.data.cityBorn)

                await trigger()
                reset(response.data)
            } catch (error: any) {
                // Solo mostrar error si no es un 404 (datos no encontrados)
                // Un 404 es normal cuando el aspirante no ha llenado el formulario
                if (error?.status !== 404) {
                    console.error('Error fetching demographic data:', error)
                }
            } finally {
                hideLoader()
            }
        }

        loadDemographicData()
    }, [idAspirant, reset, watch, trigger])

    // Prefetch nationality list once and cache it
    useEffect(() => {
        let mounted = true
        const run = async (): Promise<void> => {
            const response = await fetchNationalitiesList(mounted)

            if (mounted && response && response.length > 0) {
                setNationalityOptions(response)
                try {
                    sessionStorage.setItem(
                        'nationalityOptions',
                        JSON.stringify(response)
                    )
                } catch { }
            }
        }

        if (nationalityOptions.length === 0) {
            run()
        }

        return () => {
            mounted = false
        }
    }, [])

    // Efecto para limpiar cityBorn cuando no hay ciudades disponibles
    useEffect(() => {
        if (bornCities.length === 0) {
            setValue('cityBorn', '')
        }
    }, [bornCities, setValue])

    const onSubmit: SubmitHandler<DemographicFormData> = async (
        data: DemographicFormData
    ) => {
        // Transformar cityBorn a formato "código-nombre" antes de limpiar
        if (data.cityBorn && bornCities.length > 0) {
            const cityOption = bornCities.find(
                (city) => city.value === data.cityBorn
            )

            if (cityOption) {
                data.cityBorn = `${cityOption.value}-${cityOption.label}`
            }
        }

        // Limpieza de datos antes de enviar
        const cleanData = cleanDemographicData(data)

        console.log('dataResidence Limpio', cleanData)
        showLoader()
        const response = await aspirantsApi.postDemographicData(
            idAspirant!,
            cleanData,
            idAsesor || 0
        )

        hideLoader()
        if (response.status === 200) {
            showToast({
                title: t('basicDataInformation.title'),
                message: t('forms.ui.save'),
                type: 'success'
            })

            // Actualizar el estado de los formularios antes de continuar
            if (idAspirant) {
                aspirantsApi
                    .getStatusForms(idAspirant, 0) // No hay idInscription en DemographicForm
                    .then(() =>
                        console.log(
                            'Form status updated after Demographic completion'
                        )
                    )
                    .catch((error) =>
                        console.error('Error updating form status:', error)
                    )
            }

            onNext()
        }
    }

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full space-y-4"
            >
                <BornControl
                    control={control}
                    watchCountryBorn={watch('countryBorn')}
                    watchDepartmentBorn={watch('departmentBorn')}
                    text={text}
                    setBornCities={setBornCities}
                    onFieldChange={handleFieldChange}
                    setValue={(field, value) => setValue(field as any, value)}
                    nationalityOptions={nationalityOptions}
                    trigger={trigger} // Added trigger prop
                />
                <ResidenceControl
                    control={control}
                    watchIdCityResidence={watch('idCityResidence')}
                    watchDepartmentResidence={watch('departmentResidence')}
                    watchCountryResidence={watch('countryResidence')}
                    text={text}
                    onFieldChange={handleFieldChange}
                />
                <HousingControl
                    control={control}
                    watchCountryResidence={watch('countryResidence')}
                    watchDepartmentResidence={watch('departmentResidence')}
                />

                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                    submitLabel={t('forms.ui.nextPage')}
                />
            </form>
        </div>
    )
}
